// API service for backend communication
const API_BASE_URL = 'https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function';

class ApiService {
  // Helper method to make API calls
  async makeRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Log API call details
    console.log(`🌐 FRONTEND API CALL: ${options.method || 'GET'} ${endpoint}`, {
      url,
      method: options.method || 'GET',
      body: options.body ? JSON.parse(options.body) : null,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      // Log successful response
      console.log(`✅ FRONTEND API SUCCESS: ${options.method || 'GET'} ${endpoint}`, {
        status: response.status,
        data: data,
        timestamp: new Date().toISOString()
      });
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      // Log error response
      console.error(`❌ FRONTEND API ERROR: ${options.method || 'GET'} ${endpoint}`, {
        error: error.message,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  // ===== TALENTS API =====
  
  // Get all talents with optional search
  async getTalents(searchQuery = '') {
    console.log('🎯 TALENTS API: getTalents called', { searchQuery });
    const endpoint = searchQuery ? `/list?q=${encodeURIComponent(searchQuery)}` : '/list';
    return this.makeRequest(endpoint);
  }

  // Add new talent
  async addTalent(talentData) {
    console.log('🎯 TALENTS API: addTalent called', { talentData });
    return this.makeRequest('/add', {
      method: 'POST',
      body: JSON.stringify(talentData),
    });
  }

  // Update existing talent
  async updateTalent(talentData) {
    console.log('🎯 TALENTS API: updateTalent called', { talentData });
    return this.makeRequest('/update', {
      method: 'PUT',
      body: JSON.stringify(talentData),
    });
  }

  // Delete talent
  async deleteTalent(rowId) {
    console.log('🎯 TALENTS API: deleteTalent called', { rowId });
    return this.makeRequest('/delete', {
      method: 'DELETE',
      body: JSON.stringify({ ROWID: rowId }),
    });
  }

  // ===== PROFILES API =====
  
  // Get talent profile by talentId
  async getTalentProfile(talentId) {
    console.log('👤 PROFILES API: getTalentProfile called', { talentId });
    return this.makeRequest(`/profile?talentId=${encodeURIComponent(talentId)}`);
  }

  // Update talent profile
  async updateTalentProfile(profileData) {
    console.log('👤 PROFILES API: updateTalentProfile called', { profileData });
    return this.makeRequest('/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // ===== BROADCASTS API =====
  
  // Get all broadcasts with optional search and pagination
  async getBroadcasts(searchQuery = '', limit = 100, page = 1) {
    console.log('📢 BROADCASTS API: getBroadcasts called', { searchQuery, limit, page });
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (limit) params.append('limit', limit);
    if (page) params.append('page', page);
    
    const endpoint = `/broadcasts${params.toString() ? '?' + params.toString() : ''}`;
    return this.makeRequest(endpoint);
  }

  // Add new broadcast
  async addBroadcast(broadcastData) {
    console.log('📢 BROADCASTS API: addBroadcast called', { broadcastData });
    return this.makeRequest('/broadcasts', {
      method: 'POST',
      body: JSON.stringify(broadcastData),
    });
  }

  // Update broadcast by ID
  async updateBroadcast(id, broadcastData) {
    console.log('📢 BROADCASTS API: updateBroadcast called', { id, broadcastData });
    return this.makeRequest(`/broadcasts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(broadcastData),
    });
  }

  // Delete broadcast by ID
  async deleteBroadcast(id) {
    console.log('📢 BROADCASTS API: deleteBroadcast called', { id });
    return this.makeRequest(`/broadcasts/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== HEROES API =====
  
  // Get heroes (talents from shortlist with specific stages)
  async getHeroes(userId) {
    console.log('🦸 HEROES API: getHeroes called', { userId });
    if (!userId) {
      throw new Error('User ID is required to get heroes');
    }
    return this.makeRequest(`/heroes?userId=${encodeURIComponent(userId)}`);
  }

  // ===== HEALTH CHECK =====
  
  // Check if API is working
  async healthCheck() {
    console.log('💚 HEALTH API: healthCheck called');
    return this.makeRequest('/health');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
